import { CommonModule } from '@angular/common';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { Component, inject, OnInit, ElementRef, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PagesService } from '@services/pages.service';
import { SharedService } from '@services/shared.service';
import { SharedModule } from '@shared/shared.module';
import { AuthService } from 'src/app/Auth/AuthService';
import { PlanService } from '@services/plan.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { PrivacyPolicyComponent } from '@pages/Outer/pages/privacy-policy/privacy-policy.component';
import { TermsConditionComponent } from '@pages/Outer/pages/terms-condition/terms-condition.component';
import { SecureStorageService } from '@services/secure-storage.service';
import { TranslateService } from '@ngx-translate/core';
import {
  WebQrPlan,
  WebQrSubscriptionService,
} from '@services/web-qr-subscription.service';
import { hasCompanionAccess } from 'src/app/utils/companion-access.util';

@Component({
  selector: 'app-subscription',
  standalone: true,
  imports: [SharedModule, CommonModule, NzModalModule, ReactiveFormsModule, NzSelectModule],
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
})
export class SubscriptionComponent implements OnInit {
  authService = inject(AuthService);
  sharedService = inject(SharedService);
  pagesService = inject(PagesService);
  planService = inject(PlanService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  renderer = inject(Renderer2);
  elementRef = inject(ElementRef);
  modal = inject(NzModalService);
  secureStorage = inject(SecureStorageService);
  translateService = inject(TranslateService);
  webQrSubscriptionService = inject(WebQrSubscriptionService);
  subscribedPlans: any = {};
  activeTabId: 'monthly' | 'annually' = 'monthly';
  selectedPlan: any = {};
  isVisible = false;
  monthlyPlans: any[] = [];
  annualPlans: any[] = [];
  enterprizePlans: any[] = [];
  isLoggedIn = false;
  isPaymentModal = false;
  selectedPaymentMethod: string = '';
  renewModal: boolean = false;
  enterprizeModal: boolean = false;
  enterprizeForm!: FormGroup;
  agreeTerms: boolean = false;
  user: any;
  remainingTime: any;
  isLoadingPlans: boolean = true;
  isFreePlanModalVisible = false;
  selectedFreePlan: any = null;
  private accessDeniedShown = false;
  planChangeType: 'upgrade' | 'downgrade' | '' = '';
  // The live subscription row (2302) — carries the id and cancelled_at the manage row needs.
  mySubscription: any = null;
  isHistoryModal = false;
  historyLoading = false;
  planHistory: any[] = [];
  isCancelModal = false;
  cancelReason = '';
  cancelling = false;

  constructor(private fb: FormBuilder) {
    if (this.authService.isLoggedIn()) {
      this.isLoggedIn = true;
    }
    // ✅ Only check query params once on component initialization
    this.route.queryParams.subscribe((params) => {
      // Back from Stripe without paying: say so, rather than landing silently on the plans.
      if (params['checkout'] === 'cancelled') {
        this.sharedService.createNotification(
          'info',
          this.translateService.instant('subscription-page.checkout-cancelled-msg')
        );
        this.router.navigate([], {
          queryParams: { checkout: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
      const denied = params['accessDenied'];
      // Re-armed once the parameter is cleared, so every blocked page gets its own message.
      if (!denied) this.accessDeniedShown = false;
      if (denied && !this.accessDeniedShown) {
        this.accessDeniedShown = true;
        // 'none' = the account never had a plan (a new signup); anything else = it expired.
        if (denied === 'none') {
          this.sharedService.createNotification(
            'info',
            this.translateService.instant('subscription-page.no-plan-msg')
          );
        } else {
          // A popup, not a toast: the client was sent here from the page they asked for, and
          // should be told plainly why before they look at the plans.
          this.modal.warning({
            nzTitle: this.translateService.instant('subscription-page.expired-modal-title'),
            nzContent: this.translateService.instant('subscription-page.expired-modal-desc'),
            nzCentered: true,
            nzClassName: 'contest-styled-modal',
            nzOkText: this.translateService.instant('subscription-page.expired-modal-ok'),
          });
        }
        // Clear the query parameter to prevent showing the message again
        this.router.navigate([], {
          queryParams: { accessDenied: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    });
  }
  ngOnInit() {
    this.user = this.authService.getUserDetails();
    // Validators.required accepts "   ", so the free-text fields also need a non-blank character.
    const notBlank = Validators.pattern(/\S/);
    this.enterprizeForm = this.fb.group({
      plan_id: [null],
      name: [
        '',
        [
          Validators.required,
          notBlank,
          Validators.minLength(3),
        ],
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          // Validators.email accepts "name@domain" with no TLD; require one.
          Validators.pattern(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/),
        ]
      ],
      phone_number: [
        '',
        [
          Validators.required,
          // The template already shows 'phone-digits' for a pattern error; nothing raised it.
          Validators.pattern(/^\+?[0-9]{10,15}$/),
        ]
      ],
      company_name: ['', [Validators.required, notBlank]],
      address: ['', [Validators.required, notBlank]],
      payment_method: ['', Validators.required],
      details: ['', [Validators.required, notBlank]]
    });

    this.prefillEnterpriseForm();

    if (this.isLoggedIn) {
      this.getSubscribedPlans();
    }
    this.getSubscriptionPlans();
    this.loadWebQrPlans();
  }

  // -- Web QR plans ---------------------------------------------------------
  // A separate subscription from the campaign plan above: its own plans, its
  // own billing period, its own actions (2480-2485). Nothing here reads or
  // writes the campaign plan, so buying one leaves the other as it was.

  webQrPlans: WebQrPlan[] = [];
  /** The client's Web QR plan as the Web QR list reports it. A uuid. */
  webQrCurrentPlanId: string | null = null;
  webQrPlansLoading = false;
  webQrPlansError = '';
  /** Plan id currently being checked out, so only its button shows progress. */
  webQrBusyPlanId: string | null = null;

  /**
   * The Web QR plan whose purchase is going through the shared "Select Payment
   * Method" modal. Non-null means handlePaymentOk() routes to the Web QR
   * actions instead of the campaign-plan ones.
   */
  webQrSelectedPlan: WebQrPlan | null = null;

  loadWebQrPlans(): void {
    this.webQrPlansLoading = true;
    this.webQrPlansError = '';
    // 'subscription' is the plan type this page sells; it is not a billing period.
    this.webQrSubscriptionService.getPlans('subscription').subscribe({
      next: (result) => {
        // Only the catalogue comes from here. Which plan is current comes from PURCHASED_PLAN,
        // the one answer for both products, so only one card can ever be marked current.
        this.webQrPlans = result.plans;
        this.webQrPlansLoading = false;
      },
      error: (err: any) => {
        console.warn('[Subscription] Failed to load Web QR plans:', err);
        this.webQrPlans = [];
        this.webQrPlansError = err?.error?.message
          || this.translateService.instant('webQrPlans.load-failed');
        this.webQrPlansLoading = false;
      },
    });
  }

  /** Whether the plan in force is a Web QR plan, per PURCHASED_PLAN's active_product. */
  isOnWebQrPlan(): boolean {
    return this.subscribedPlans?.active_product === 'web_qr';
  }

  /**
   * Whether this is the client's current plan. Answered by PURCHASED_PLAN, which
   * reports ONE plan in force across both products: a Web QR card is current only
   * while active_product is 'web_qr' and plan_id is this plan.
   */
  isCurrentWebQrPlan(plan: WebQrPlan): boolean {
    return this.isOnWebQrPlan() && !this.remainingTime?.expired &&
      this.isSameId(this.webQrCurrentPlanId, plan.id);
  }

  private get currentWebQrPlanCard(): WebQrPlan | null {
    return this.webQrPlans.find((plan) => this.isCurrentWebQrPlan(plan)) ?? null;
  }

  /**
   * Where a plan sits relative to the one the client holds: the same plan, a
   * dearer one, or a cheaper one. '' when no direction can honestly be claimed
   * -- no plan held, or the two are priced in different currencies.
   */
  webQrPlanComparison(plan: WebQrPlan): 'upgrade' | 'downgrade' | 'same' | '' {
    const current = this.currentWebQrPlanCard;
    if (!current) return '';
    if (current.id === plan.id) return 'same';
    if (!current.currency || current.currency !== plan.currency) return '';
    if (plan.priceAmount === current.priceAmount) return 'same';
    return plan.priceAmount > current.priceAmount ? 'upgrade' : 'downgrade';
  }

  /**
   * What a Web QR card's button reads: the plan in force offers Renew, the
   * others Upgrade or Downgrade, and with no plan held every card offers Subscribe.
   */
  webQrPlanActionLabel(plan: WebQrPlan): string {
    if (this.isCurrentWebQrPlan(plan)) return 'webQrPlans.renew';
    switch (this.webQrPlanComparison(plan)) {
      case 'upgrade': return 'Plan&Price.upgradeBtn';
      case 'downgrade': return 'Plan&Price.downgradeBtn';
      default: return 'webQrPlans.subscribe';
    }
  }

  /** The plan's period ('quarter') as a translated word, or the raw word if unknown. */
  webQrPricePerLabel(plan: WebQrPlan): string {
    const per = (plan.pricePer || '').toLowerCase();
    const known: Record<string, string> = {
      month: 'webQrPlans.per-month',
      monthly: 'webQrPlans.per-month',
      quarter: 'webQrPlans.per-quarter',
      quarterly: 'webQrPlans.per-quarter',
      year: 'webQrPlans.per-year',
      yearly: 'webQrPlans.per-year',
      annual: 'webQrPlans.per-year',
    };
    return known[per] ? this.translateService.instant(known[per]) : plan.pricePer;
  }

  getWebQrPlanDisplayName(plan: WebQrPlan): string {
    const name = plan.name.trim();
    const key = `webQrPlans.plan-names.${name.toLowerCase().replace(/\s+/g, '-')}`;
    const translated = this.translateService.instant(key);
    return translated === key ? plan.name : translated;
  }

  webQrDisplayValue(value: string): string {
    const key = `webQrPlans.values.${value.trim().toLowerCase()}`;
    const translated = this.translateService.instant(key);
    return translated === key ? value : translated;
  }

  webQrFeatureParams(params?: Record<string, unknown>): Record<string, unknown> {
    const localized = { ...params };
    const separator = this.translateService.currentLang === 'ar' ? '، ' : ', ';
    for (const field of ['level', 'tier', 'channels', 'currency']) {
      const value = localized[field];
      if (typeof value === 'string') {
        localized[field] = value.split(',').map(part => this.webQrDisplayValue(part.trim())).join(separator);
      }
    }
    return localized;
  }

  isWebQrPlanBusy(plan: WebQrPlan): boolean {
    return this.webQrBusyPlanId === plan.id;
  }

  /**
   * Starts a Web QR purchase through the same "Select Payment Method" modal as
   * the campaign plans; handlePaymentOk() then routes it to the Web QR actions.
   */
  onWebQrPlanAction(plan: WebQrPlan): void {
    if (!this.isLoggedIn) {
      this.modal.warning({
        nzTitle: this.translateService.instant('subscription-page.login-required-title'),
        nzContent: this.translateService.instant('subscription-page.login-required-desc'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-warning',
        nzOkText: this.translateService.instant('subscription-page.login-btn'),
        nzOnOk: () => this.router.navigate(['auth/login']),
      });
      return;
    }
    // The modal is shared, so it must not open carrying the previous choice.
    this.selectedPaymentMethod = '';
    this.agreeTerms = false;
    this.webQrSelectedPlan = plan;
    this.isPaymentModal = true;
  }

  /**
   * The Web QR half of handlePaymentOk(). Returns false when this is not a Web
   * QR purchase, so the campaign-plan path continues untouched.
   */
  private handleWebQrPaymentOk(): boolean {
    const plan = this.webQrSelectedPlan;
    if (!plan) return false;
    this.webQrSelectedPlan = null;

    if (this.selectedPaymentMethod === 'online') {
      this.checkoutWebQrPlan(plan);
    } else if (this.selectedPaymentMethod === 'cash') {
      this.modal.confirm({
        nzTitle: this.translateService.instant('subscription-page.cash-payment'),
        nzContent: this.translateService.instant('subscription-page.cash-payment-confirm-desc'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOkText: this.translateService.instant('subscription-page.proceed'),
        nzCancelText: this.translateService.instant('subscription-page.cancel'),
        nzOnOk: () => this.requestWebQrCashPayment(plan),
      });
    }
    return true;
  }

  /** Online payment: subscribe, or renew when it is already the client's plan. */
  private checkoutWebQrPlan(plan: WebQrPlan): void {
    const base = this.sharedService.getBaseUrl();
    // flow=web_qr tells the return page to confirm on the Web QR action (2483),
    // not on the campaign plan's CHECK_PAYMENT.
    const successUrl = base + '/auth/payment-approved?flow=web_qr&session_id={CHECKOUT_SESSION_ID}';
    const cancelUrl = base + '/home/subscription?checkout=cancelled';
    const renewing = this.isCurrentWebQrPlan(plan);

    this.webQrBusyPlanId = plan.id;
    this.sharedService.showLoader();

    const request = renewing
      ? this.webQrSubscriptionService.renew(plan.id, successUrl, cancelUrl)
      : this.webQrSubscriptionService.subscribe(plan.id, successUrl, cancelUrl);

    request.subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        const checkoutUrl = res?.checkout_url || res?.url || res?.payment_url;
        if (checkoutUrl) {
          window.location.href = checkoutUrl;
          return;
        }
        this.loadWebQrPlans();
        this.sharedService.createNotification(
          'warning',
          this.translateService.instant('subscription-page.checkout-url-not-found')
        );
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        this.sharedService.createNotification(
          'error',
          err?.error?.message || err?.message || this.translateService.instant('webQrPlans.checkout-failed')
        );
      },
    });
  }

  /**
   * Cash payment: records a request for an admin to approve. It activates
   * nothing, so the list is refreshed to show where things stand.
   */
  private requestWebQrCashPayment(plan: WebQrPlan): void {
    this.webQrBusyPlanId = plan.id;
    this.sharedService.showLoader();

    this.webQrSubscriptionService.requestCashPayment(plan.id, '').subscribe({
      next: () => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        this.modal.success({
          nzTitle: this.translateService.instant('subscription-page.cash-request-submitted-title'),
          nzContent: this.translateService.instant('webQrPlans.cash-requested'),
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-success',
          nzOkText: this.translateService.instant('common.ok'),
        });
        this.loadWebQrPlans();
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        this.webQrBusyPlanId = null;
        this.sharedService.createNotification(
          'error',
          err?.error?.message || err?.message || this.translateService.instant('webQrPlans.cash-failed')
        );
      },
    });
  }

  freePlanApi(planId: number) {
    const user = this.authService.getUserDetails();
    if (!user) return;
    const payload = {
      user_id: user.id,
      plan_id: planId
    };
    this.sharedService.showLoader();
    this.pagesService.create('FREE_PLAN', payload).subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'success',
          this.translateService.instant('subscription-page.free-plan-subscribed-success')
        );

        // ✅ Update premimum_user flag in localStorage
        const userDetails = this.authService.getUserDetails();
        if (userDetails) {
          userDetails.premimum_user = true;
          void this.secureStorage.setItem('userDetails', userDetails);
        }

        this.sharedService.updatedUserData.next(true);
        this.getSubscribedPlans();
      },
      error: (err: any) => {
        this.sharedService.hideLoader();

        // Check multiple possible error structures
        const errorCode = err?.error?.code || err?.code;
        const errorMessage = err?.error?.message || err?.message;

        if (errorCode === 'ERROR_FATAL') {
          this.sharedService.createNotification(
            'error',
            errorMessage || this.translateService.instant('subscription-page.free-plan-subscribe-failed')
          );
        } else {
          // Show generic error message if it's a different error
          this.sharedService.createNotification(
            'error',
            errorMessage || this.translateService.instant('subscription-page.free-plan-subscribe-failed')
          );
        }
      }
    });
  }
  // free plan timmer function
  isFreePlan(): boolean {
    if (!this.subscribedPlans || !this.subscribedPlans.sub_plan) {
      return false;
    }
    const subPlan = this.subscribedPlans.sub_plan;
    // Number(): sub_plan prices can be numeric strings too.
    const bothPricesFree = Number(subPlan.monthly_price) === 0 && Number(subPlan.yearly_price) === 0;
    const descriptionContainsFree = subPlan.description &&
      subPlan.description.toLowerCase().includes('free');
    const planNameContainsFree = subPlan.plan_name &&
      subPlan.plan_name.toLowerCase().includes('free');
    return bothPricesFree || descriptionContainsFree || planNameContainsFree;
  }
  showModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    this.isVisible = false;
    this.webQrSelectedPlan = null;
    this.isPaymentModal = true;
    this.planChangeType = '';
  }
  handlePaymentOk(): void {
    if (!this.selectedPaymentMethod) {
      this.sharedService.createNotification(
        'error',
        this.translateService.instant('subscription-page.select-payment-method-required')
      );
      return;
    }
    this.isPaymentModal = false;
    if (this.handleWebQrPaymentOk()) return;
    const isRenewFlow = !!this.selectedPlan?.isRenewal;

    if (isRenewFlow) {
      this.renewCurrentPlan(this.selectedPlan.plan_id, this.selectedPaymentMethod);
      return;
    }

    if (this.selectedPaymentMethod === 'online') {
      // Moving off a live plan is a plan CHANGE (2306): it records where the client came from
      // and can land on a free plan too. A first purchase stays on subscribe (2304).
      if (this.hasLiveSubscription()) {
        this.changePlan(this.selectedPlan.plan_id);
      } else {
        this.subscribAPlan(this.selectedPlan.plan_id);
      }
    } else if (this.selectedPaymentMethod === 'cash') {
      this.modal.confirm({
        nzTitle: this.translateService.instant('subscription-page.cash-payment'),
        nzContent: this.translateService.instant('subscription-page.cash-payment-confirm-desc'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal',
        nzOkText: this.translateService.instant('subscription-page.proceed'),
        nzCancelText: this.translateService.instant('subscription-page.cancel'),
        nzOnOk: () => {
          this.cashPayment(this.selectedPlan.plan_id);
        }
      });
    }
  }
  cashPayment(plan_id: string) {
    if (!this.isLoggedIn) {
      this.modal.warning({
        nzTitle: this.translateService.instant('subscription-page.login-required-title'),
        nzContent: this.translateService.instant('subscription-page.login-required-desc'),
        nzCentered: true,
        nzClassName: 'contest-styled-modal contest-modal-warning',
        nzOkText: this.translateService.instant('subscription-page.login-btn'),
        nzOnOk: () => {
          this.router.navigate(['auth/login']);
        }
      });
      return;
    }
    const payload = {
      plan_id,
      type: this.activeTabId
    };
    this.sharedService.showLoader();
    this.pagesService.create('CASH_PAYMENT', payload).subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();
        this.isPaymentModal = false;
        // The request is queued for an admin; without this the dialog just closed and the
        // business had no idea whether anything happened. `reused` = one was already open.
        this.modal.success({
          nzTitle: this.translateService.instant('subscription-page.cash-request-submitted-title'),
          nzContent: this.translateService.instant(
            res?.reused ? 'subscription-page.cash-request-exists-desc' : 'subscription-page.cash-request-submitted-desc'
          ),
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-success',
          nzOkText: this.translateService.instant('common.ok')
        });
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.modal.error({
          nzTitle: this.translateService.instant('subscription-page.error-title'),
          nzContent: error?.error?.message || this.translateService.instant('subscription-page.cash-payment-init-failed'),
          nzCentered: true,
          nzClassName: 'contest-styled-modal contest-modal-error',
          nzOkText: this.translateService.instant('common.ok')
        });
      },
    });
  }
  handleCancel(): void {
    this.isVisible = false;
    this.selectedPlan = null;
    this.planChangeType = '';
  }
  handleRenewCancel(): void {
    this.renewModal = false;
  }
  /** True once the user has left the field (or pressed Submit) and it still fails validation. */
  isEnterpriseFieldInvalid(name: string): boolean {
    const control = this.enterprizeForm?.get(name);
    return !!control && control.invalid && control.touched;
  }
  handleEnterprizeCancel(): void {
    this.enterprizeModal = false;
    this.enterprizeForm.reset();
  }
  handleRenewOk(): void {
    this.renewModal = false;
    this.webQrSelectedPlan = null;
    this.isPaymentModal = true;
  }
  handleEnterprizeOk(): void {
    if (!this.enterprizeForm.valid) {
      this.sharedService.hideLoader();
      this.enterprizeForm.markAllAsTouched();
      return;
    }
    const form = this.enterprizeForm;
    const basePayload = {
      plan_id: form.get('plan_id')?.value,
      name: form.get('name')?.value,
      email: form.get('email')?.value,
      phone_number: form.get('phone_number')?.value,
      details: form.get('details')?.value,
      company_name: form.get('company_name')?.value,
      address: form.get('address')?.value,
      payment_method: form.get('payment_method')?.value,
    };
    this.sharedService.showLoader();
    this.pagesService.update('ENTERPRISE_PLAN', basePayload).subscribe({
      next: () => {
        this.sharedService.createNotification(
          'success',
          this.translateService.instant('subscription-page.enterprise-request-success')
        );
        this.enterprizeForm.reset();
        this.enterprizeModal = false;
        this.sharedService.hideLoader();
      },
      error: (err: any) => {
        const backendMessage = err?.error?.message || err?.message || '';
        this.sharedService.createNotification(
          'error',
          backendMessage || this.translateService.instant('subscription-page.enterprise-request-failed')
        );
        this.sharedService.hideLoader();
      },
    });
  }
  handlePaymentCancel(): void {
    this.isPaymentModal = false;
    this.webQrSelectedPlan = null;
    this.selectedPaymentMethod = '';
  }
  subscribe(plan_id: string, type: string, mode: string, price: string) {
    if (!this.isLoggedIn) {
      this.sharedService.createNotification(
        'warning',
        this.translateService.instant('subscription-page.subscribe-login-warning')
      );
      this.router.navigate(['auth/login']);
      return;
    }
    this.selectedPlan = { plan_id, type, mode, price };
    this.webQrSelectedPlan = null;
    if (this.subscribedPlans && this.subscribedPlans.type) {
      this.isVisible = true;
    } else {
      this.isPaymentModal = true;
    }
  }
  subscribAPlan(plan_id: string) {
    const type = {
      plan_id,
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/home/subscription?checkout=cancelled`,
      type: this.activeTabId,
    };
    this.sharedService.showLoader();
    this.authService.login('CHOOSE_SUBSCRIPTION_PLAN', type).subscribe({
      next: (Response: any) => {
        setTimeout(() => {
          this.sharedService.hideLoader();
          if (Response.checkout_url) {
            window.location.href = Response.checkout_url;
          } else {
            this.sharedService.createNotification(
              'error',
              this.translateService.instant('subscription-page.checkout-url-not-found')
            );
          }
        }, 1000);
        const alertsJson = localStorage.getItem('app_alerts');
        if (alertsJson) {
          let alerts = JSON.parse(alertsJson);
          alerts = alerts.filter(
            (alert: any) => alert.action !== 'subscription'
          );
          localStorage.setItem('app_alerts', JSON.stringify(alerts));
        }
        this.sharedService.closeAlertByAction('subscription');
      },
      error: (error: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          error?.error?.message || this.translateService.instant('subscription-page.subscribe-init-failed')
        );
      },
    });
  }
  hasLiveSubscription(): boolean {
    return String(this.mySubscription?.status || '').toLowerCase() === 'active';
  }

  isCancelScheduled(): boolean {
    return this.hasLiveSubscription() && !!this.mySubscription?.cancelled_at;
  }

  loadMySubscription() {
    this.pagesService.getAll('MY_SUBSCRIPTION').subscribe({
      next: (res: any) => {
        this.mySubscription = res?.subscription || null;
      },
      error: () => {
        this.mySubscription = null;
      },
    });
  }

  changePlan(plan_id: string) {
    const payload = {
      plan_id,
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/home/subscription?checkout=cancelled`,
      type: this.activeTabId,
    };
    this.sharedService.showLoader();
    this.pagesService.create('CHANGE_PLAN', payload).subscribe({
      next: (res: any) => {
        this.sharedService.hideLoader();
        if (res?.payment_required) {
          if (res?.checkout_url) {
            window.location.href = res.checkout_url;
          } else {
            this.sharedService.createNotification(
              'error',
              this.translateService.instant('subscription-page.checkout-url-not-found')
            );
          }
          return;
        }
        // Nothing to pay (a move onto a free plan) — the change is already live.
        this.sharedService.createNotification(
          'success',
          this.translateService.instant('subscription-page.plan-changed-success')
        );
        this.sharedService.updatedUserData.next(true);
        this.getSubscribedPlans();
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification(
          'error',
          err?.error?.message || this.translateService.instant('subscription-page.subscribe-init-failed')
        );
      },
    });
  }

  openPlanHistory() {
    this.isHistoryModal = true;
    this.historyLoading = true;
    this.pagesService.getAll('SUBSCRIPTION_HISTORY').subscribe({
      next: (res: any) => {
        this.planHistory = res?.subscriptions || [];
        this.historyLoading = false;
      },
      error: (err: any) => {
        this.planHistory = [];
        this.historyLoading = false;
        this.sharedService.createNotification(
          'error',
          err?.error?.message || this.translateService.instant('subscription-page.history-load-failed')
        );
      },
    });
  }

  historyAmount(row: any): string {
    // `amount` is the plan price in whole currency units (plan_prices.amount_usd_cents holds
    // dollars despite its name — the plans page and the Stripe checkout both read it that way).
    const amount = Number(row?.amount);
    if (!isFinite(amount) || amount === 0) return this.translateService.instant('subscription-page.plan-names.free');
    return `${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${row?.currency || 'USD'}`;
  }

  openCancelPlan() {
    this.cancelReason = '';
    this.isCancelModal = true;
  }

  confirmCancelPlan() {
    if (this.cancelling) return;
    this.cancelling = true;
    this.pagesService.create('CANCEL_SUBSCRIPTION', {
      subscription_id: this.mySubscription?.id,
      reason: this.cancelReason.trim(),
    }).subscribe({
      next: (res: any) => {
        this.cancelling = false;
        this.isCancelModal = false;
        // access_until can come back empty, so fall back to the period end the page already shows.
        const until = this.formatExpiryDate(
          res?.access_until || res?.subscription?.end_date || this.subscribedPlans?.end_date
        );
        this.sharedService.createNotification(
          'success',
          this.translateService.instant('subscription-page.cancel-plan-success', { date: until })
        );
        this.mySubscription = res?.subscription || this.mySubscription;
        this.loadMySubscription();
      },
      error: (err: any) => {
        this.cancelling = false;
        this.sharedService.createNotification(
          'error',
          err?.error?.message || this.translateService.instant('subscription-page.cancel-plan-failed')
        );
      },
    });
  }

  getSubscribedPlans() {
    this.loadMySubscription();
    this.isLoadingPlans = true;
    this.sharedService.showLoader();
    this.authService.getAll('PURCHASED_PLAN').subscribe({
      next: (Response: any) => {
        this.subscribedPlans = Response || null;
        this.webQrCurrentPlanId = this.isOnWebQrPlan() ? String(this.subscribedPlans?.plan_id ?? '') || null : null;

        if (this.subscribedPlans) {
          const isExpiredFromApi = typeof this.subscribedPlans?.isExpired === 'boolean'
            ? this.subscribedPlans.isExpired
            : null;

          // If API explicitly marks expired, honor it; otherwise calculate days left
          this.remainingTime = isExpiredFromApi === true
            ? { days: 0, expired: true }
            : this.calculateRemainingTime(this.subscribedPlans);

          if (this.remainingTime.expired) {
            this.sharedService.createNotification(
              'warning',
              this.translateService.instant('subscription-page.subscription-expired-msg')
            );
          }
        }

        const userPlan = Response?.report_view?.toLowerCase() || 'none';
        this.sharedService.setPlanType(userPlan);
        this.isLoadingPlans = false;
        this.sharedService.hideLoader();
        this.setActiveTabBasedOnSubscription();
      },
      error: (error: any) => {
        this.isLoadingPlans = false;
        this.sharedService.hideLoader();

        // The gateway wraps business failures as { error: { code, message } }, so the old
        // `error.message` test never matched and a new account kept a stale plan state.
        if (error?.error?.code === 'NOT_SUBSCRIBED' || error?.error?.message === 'User is not subscribed yet') {
          this.subscribedPlans = {};
          this.webQrCurrentPlanId = null;
          this.sharedService.setPlanType('none');
          this.setActiveTabBasedOnSubscription();
        }
      },
    });
  }

  getSubscriptionPlans() {
    const getOrderIndex = (price: any): number => {
      if (price == null) return Number.MAX_SAFE_INTEGER;
      if (typeof price === 'string') {
        const lower = price.toLowerCase().trim();
        if (lower === 'free') return 0;
        if (lower.includes('contact')) return Number.MAX_SAFE_INTEGER;
        const numeric = parseFloat(lower.replace(/[^0-9.]/g, ''));
        return isNaN(numeric) ? Number.MAX_SAFE_INTEGER : numeric;
      }
      return price;
    };
    const sortPlans = (plans: any[], priceKey: 'monthly_price' | 'yearly_price') => {
      return plans.sort((a: any, b: any) => {
        // Enterprise always last
        if (a.isEnterprise !== b.isEnterprise) {
          return a.isEnterprise ? 1 : -1;
        }
        // Then sort by monthly/yearly price
        return getOrderIndex(a?.[priceKey]) - getOrderIndex(b?.[priceKey]);
      });
    };
    this.sharedService.showLoader();
    this.pagesService.getAll('GET_SUBSCRIPTION_PLAN', {
      page: 1,
      per_page: 1000
    }).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        const plans = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : Array.isArray(response?.data)
              ? response.data
              : [];

        if (plans.length > 0) {
          // Prices arrive as numeric strings ("0", "4900"); every `=== 0` free-plan check
          // (the free-plan dialog, the "Free" label) silently failed on them, sending the free
          // Starter plan to paid checkout. Non-numeric labels like "Contact us" are left alone.
          const toNumber = (v: any) =>
            typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v)) ? Number(v) : v;
          const activePlans = plans
            .filter((plan: any) => plan?.status?.toLowerCase() === 'active')
            .map((plan: any) => ({
              ...plan,
              monthly_price: toNumber(plan.monthly_price),
              yearly_price: toNumber(plan.yearly_price),
            }));
          // Use monthly_price & yearly_price instead of mode. Copies: sort() works in place,
          // so both tabs were sharing one array in yearly order.
          this.monthlyPlans = sortPlans([...activePlans], 'monthly_price');
          this.annualPlans = sortPlans([...activePlans], 'yearly_price');

          // Enterprise plans separately if you want
          this.enterprizePlans = activePlans.filter((plan: any) => plan.isEnterprise);
          // ✅ Yahan expiry check karo
          if (this.remainingTime?.expired) {
            this.sharedService.createNotification(
              'warning',
              this.translateService.instant('subscription-page.subscription-expired-warning')
            );
          }
        } else {
          this.sharedService.createNotification('error', this.translateService.instant('subscription-page.no-plans-available'));
        }
      },
      error: () => {
        this.sharedService.hideLoader();
      },
    });
  }
  isEnterprizePlan(plan: any): boolean {
    return plan?.isEnterprise === true;
  }

  // Only what the admin set on the plan; the name or price never decides it.
  hasWhatsappCompanion(plan: any): boolean {
    return hasCompanionAccess(plan);
  }
  /** Is this the billing cycle the current subscription was actually bought on? */
  isSubscribedCycle(cycle: 'monthly' | 'yearly'): boolean {
    const mode = String(this.subscribedPlans?.mode || '').toLowerCase();
    if (!mode) return false;
    const subscribedAnnual = mode === 'annually' || mode === 'yearly';
    return subscribedAnnual === (cycle === 'yearly');
  }

  setActiveTabBasedOnSubscription() {
    if (this.subscribedPlans && this.subscribedPlans.mode) {
      if (
        this.subscribedPlans.mode.toLowerCase() === 'annually' ||
        this.subscribedPlans.mode.toLowerCase() === 'yearly'
      ) {
        this.activeTabId = 'annually';
      } else {
        this.activeTabId = 'monthly';
      }
    } else {
      this.activeTabId = 'monthly';
    }
    this.updateBootstrapTabActivation();
  }
  updateBootstrapTabActivation() {
    if (typeof window !== 'undefined' && this.elementRef) {
      setTimeout(() => {
        const monthlyTabButton = this.elementRef.nativeElement.querySelector('#monthly-tab');
        const yearlyTabButton = this.elementRef.nativeElement.querySelector('#yearly-tab');
        const enterprizeTabButton = this.elementRef.nativeElement.querySelector('#enterprize-tab');
        const monthlyTabPane = this.elementRef.nativeElement.querySelector('#monthly');
        const yearlyTabPane = this.elementRef.nativeElement.querySelector('#yearly');
        const enterprizeTabPane = this.elementRef.nativeElement.querySelector('#enterprize');
        const tabs = [
          { id: 'monthly', button: monthlyTabButton, pane: monthlyTabPane },
          { id: 'annually', button: yearlyTabButton, pane: yearlyTabPane },
          { id: 'enterprize', button: enterprizeTabButton, pane: enterprizeTabPane },
        ];
        tabs.forEach(tab => {
          const isActive = this.activeTabId === tab.id;
          if (tab.button && tab.pane) {
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.button, 'active');
            this.renderer.setAttribute(tab.button, 'aria-selected', isActive.toString());
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.pane, 'show');
            this.renderer[isActive ? 'addClass' : 'removeClass'](tab.pane, 'active');
          }
        });
      }, 0);
    }
  }
  selectTab(tabId: 'monthly' | 'annually') {
    this.activeTabId = tabId;
    this.updateBootstrapTabActivation();
  }
  isCurrentPlan(id: any, mode: string): boolean {
    if (this.remainingTime?.expired) return false; // an expired plan is nobody's current plan
    // Ids as text: `currentType === id` was a uuid compared with ===, which is only true when
    // both sides are the identical string — and one side used to arrive as a number.
    const currentMode = this.subscribedPlans?.mode?.toLowerCase();
    return this.isCurrentPlanId(id) && currentMode === mode.toLowerCase();
  }

  /** Two ids, compared as ids. */
  private isSameId(a: any, b: any): boolean {
    const x = String(a ?? '').trim();
    const y = String(b ?? '').trim();
    return !!x && !!y && x.toLowerCase() === y.toLowerCase();
  }
  renewSubscribtion() {
    this.renewModal = true
  }

  isPlanExpired(): boolean {
    return !!(this.subscribedPlans?.isExpired === true || this.remainingTime?.expired);
  }

  isSamePlan(plan: any): boolean {
    return this.isCurrentPlanId(plan?.id);
  }

  // The highlight and "Your current plan" caption follow the plan, not the billing cycle: a
  // monthly subscriber still sees their plan marked on the Annual tab. getPlanComparison stays
  // cycle-aware because it drives the button action.
  isCurrentPlanCard(plan: any): boolean {
    return !this.remainingTime?.expired && this.isSamePlan(plan);
  }

  /**
   * The subscribed plan's id, AS AN ID.
   *
   * This used to be Number(rawPlanId), returning null when the result was not finite — and a
   * plan id is a uuid, so Number() gave NaN and this returned null for every subscription that
   * exists. No card was ever the current one: no badge, and every button said "upgrade".
   */
  private getCurrentPlanId(): string | null {
    // On a Web QR plan no campaign plan is in force; plan_id is then the Web QR plan's.
    if (this.isOnWebQrPlan()) return null;
    const rawPlanId =
      this.subscribedPlans?.plan_id ??
      this.subscribedPlans?.subscription_plan_id ??
      this.subscribedPlans?.sub_plan?.id ??
      this.subscribedPlans?.sub_plan?.plan_id;

    const planId = String(rawPlanId ?? '').trim();
    return planId && planId !== 'null' && planId !== 'undefined' ? planId : null;
  }

  /** Same id? Compared as text — right for a uuid, and still right for the old integers. */
  private isCurrentPlanId(planId: any): boolean {
    const currentPlanId = this.getCurrentPlanId();
    const candidate = String(planId ?? '').trim();
    return !!currentPlanId && !!candidate &&
      currentPlanId.toLowerCase() === candidate.toLowerCase();
  }

  // report_view arrives as NONE / BASIC / ADVANCED; NONE means the plan has no report access.
  hasReportAccess(plan: any): boolean {
    const level = String(plan?.report_view ?? '').trim().toLowerCase();
    return !!level && level !== 'none';
  }

  getReportAccessLabel(plan: any): string {
    if (!this.hasReportAccess(plan)) {
      return this.translateService.instant('subscription-page.not-included');
    }
    const level = String(plan.report_view).trim().toLowerCase();
    const key = `subscription-page.report-levels.${level}`;
    const label = this.translateService.instant(key);
    // A level the translations don't know yet still reads as a word, not as raw CAPS.
    return label !== key ? label : level.charAt(0).toUpperCase() + level.slice(1);
  }

  // Enterprise sends no engagement limit (null) and 0 means no cap — both read as "Unlimited".
  isUnlimitedContests(plan: any): boolean {
    const limit = plan?.Contest_allowed;
    return limit === 0 || limit === '0' || (!!plan?.isEnterprise && (limit === null || limit === undefined || limit === ''));
  }

  hasContestLimit(plan: any): boolean {
    const limit = plan?.Contest_allowed;
    return this.isUnlimitedContests(plan) || (limit !== null && limit !== undefined && limit !== '');
  }

  // A free plan (price 0 on both cycles, or a plan named "free")
  isFreePlanCard(plan: any): boolean {
    if (!plan) return false;
    const bothPricesFree = Number(plan.monthly_price) === 0 && Number(plan.yearly_price) === 0;
    const planNameFree = !!plan.plan_name && plan.plan_name.toLowerCase().includes('free');
    return bothPricesFree || planNameFree;
  }

  // Only treat the free plan as active when the active subscription itself is free.
  // The backend/user free-plan flag can be stale after a user upgrades.
  isFreePlanActive(): boolean {
    if (!this.hasActiveSubscription()) {
      return false;
    }

    const currentPlan = this.subscribedPlans?.sub_plan;
    if (currentPlan) {
      return this.isFreePlanCard(currentPlan);
    }

    const cardPlan = [...this.monthlyPlans, ...this.annualPlans].find((plan) => this.isSamePlan(plan));
    if (cardPlan) {
      return this.isFreePlanCard(cardPlan);
    }

    const currentPlanName = String(
      this.subscribedPlans?.plan_name ||
      this.subscribedPlans?.type ||
      ''
    ).toLowerCase();

    return currentPlanName.includes('free');
  }

  shouldShowRenew(plan: any): boolean {
    // A free plan never needs renewing, so it should never show the Renew action. An Enterprise
    // plan the business is already on renews like any other plan; it was agreed with sales once.
    return this.isLoggedIn && this.isSamePlan(plan) && !this.isFreePlanCard(plan);
  }

  // The "Contact Us" button is for an Enterprise plan the business is not on yet.
  showsEnterpriseContact(plan: any): boolean {
    return !!plan?.isEnterprise && !this.shouldShowRenew(plan);
  }

  getPlanActionLabel(plan: any): string {
    if (this.shouldShowRenew(plan)) return 'subscription-page.renew';
    if (this.getPlanComparison(plan.id) === 'same') return 'Plan&Price.currentPlanBtn';

    if (!this.hasActiveSubscription()) return 'subscription-page.subscribe';

    return this.getPlanComparison(plan.id) === 'upgrade' ? 'Plan&Price.upgradeBtn' : 'Plan&Price.downgradeBtn';
  }

  private titleCase(value: string): string {
    return value.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  }

  getPlanDisplayName(plan: any): string {
    const name = plan?.plan_name || plan?.type || '';
    if (!name) return '';
    const key = `subscription-page.plan-names.${name.toLowerCase()}`;
    const translated = this.translateService.instant(key);
    return translated === key ? this.titleCase(name) : translated;
  }

  /** The date pipe only knows the 'en' locale, so Arabic got an English month name. */
  formatExpiryDate(value: any): string {
    const date = new Date(value);
    if (isNaN(date.getTime())) return '';
    const lang = this.translateService.currentLang || localStorage.getItem('lang') || 'en';
    const locale = lang === 'ar' ? 'ar-EG' : 'en-US';
    return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  }

  renewCurrentPlan(plan: any, paymentMethod: string = 'online') {
    const user = this.authService.getUserDetails();

    if (!user?.id) {
      this.sharedService.createNotification('warning', this.translateService.instant('subscription-page.renew-login-warning'));
      this.router.navigate(['auth/login']);
      return;
    }

    const payload = {
      plan_id: this.subscribedPlans?.plan_id || plan?.id,
      user_id: user.id,
      billing_type: this.activeTabId === 'annually' ? 'annually' : 'monthly',
      payment_method: paymentMethod || 'online',
      success_url: `${this.sharedService.getBaseUrl()}/auth/payment-approved?session_id={CHECKOUT_SESSION_ID}`,
      cancel_url: `${this.sharedService.getBaseUrl()}/auth/payment-rejected`
    };

    this.sharedService.showLoader();
    this.pagesService.create('RENEWAL_PAYMENT_LINK', payload).subscribe({
      next: (response: any) => {
        this.sharedService.hideLoader();
        if (response?.checkout_url) {
          window.location.href = response.checkout_url;
          return;
        }
        if (payload.payment_method === 'cash') {
          this.modal.success({
            nzTitle: this.translateService.instant('subscription-page.renew-request-submitted-title'),
            // `reused` = a cash request for this plan was already open; say so instead of
            // implying a second one was filed.
            nzContent: this.translateService.instant(
              response?.reused ? 'subscription-page.cash-request-exists-desc' : 'subscription-page.renew-request-submitted-desc'
            ),
            nzCentered: true,
            nzClassName: 'contest-styled-modal contest-modal-success',
            nzOkText: this.translateService.instant('common.ok')
          });
          return;
        }
        this.sharedService.createNotification('error', this.translateService.instant('subscription-page.checkout-url-not-found'));
      },
      error: (err: any) => {
        this.sharedService.hideLoader();
        this.sharedService.createNotification('error', err?.error?.message || this.translateService.instant('subscription-page.renew-init-failed'));
      }
    });
  }

  handleClick(plan: any) {
    // An Enterprise plan is priced with sales, so renewing it goes through the contact-us form too.
    if (this.shouldShowRenew(plan) && plan.isEnterprise) {
      this.EnterprizeSubscribtion();
      this.enterprizeForm.get('plan_id')?.setValue(plan?.id);
      this.prefillEnterpriseForm();
      return;
    }

    if (this.shouldShowRenew(plan)) {
      const mode = this.activeTabId === 'annually' ? 'annually' : 'monthly';
      const renewCycle = mode === 'annually' ? 'yearly' : 'monthly';
      let price = this.getTotalPrice(plan, renewCycle);

      // Use the same price logic as the card display
      if (this.hasRenewalDiscount(plan, renewCycle)) {
        price = this.getRenewalDiscountAmount(plan, renewCycle) + this.getEffectiveSdkPrice(plan);
      } else if (plan.discount_type && plan.discount_value) {
        price = this.getDiscountedPrice(price, plan.discount_type, plan.discount_value);
      }

      this.selectedPlan = {
        plan_id: plan.id,
        type: plan.type,
        plan_name: plan.plan_name,
        mode: mode,
        price: price,
        currency: plan.currency || 'USD',
        isRenewal: true
      };
      this.renewModal = true;
      return;
    }

    if (plan.isEnterprise) {
      this.EnterprizeSubscribtion();
      this.enterprizeForm.get('plan_id')?.setValue(plan?.id);
      this.prefillEnterpriseForm();
    }
    // Free plan case
    else if (
      (plan.monthly_price === 0 && plan.yearly_price === 0) ||
      plan.plan_name.toLowerCase().includes('free')
    ) {
      this.selectedFreePlan = plan;
      this.isFreePlanModalVisible = true;
    }
    else {
      // Determine the mode and price based on active tab
      const mode = this.activeTabId === 'annually' ? 'annually' : 'monthly';
      const cycle = mode === 'annually' ? 'yearly' : 'monthly';
      let price = this.getTotalPrice(plan, cycle);

      // Use the same price logic as the card display
      if (this.hasRenewalDiscount(plan, cycle)) {
        price = this.getRenewalDiscountAmount(plan, cycle) + this.getEffectiveSdkPrice(plan);
      } else if (plan.discount_type && plan.discount_value) {
        price = this.getDiscountedPrice(price, plan.discount_type, plan.discount_value);
      }

      const hasValidSubscription = this.subscribedPlans &&
        Object.keys(this.subscribedPlans).length > 0 &&
        this.subscribedPlans.plan_id !== undefined;
      if (hasValidSubscription && !this.remainingTime?.expired) {
        this.selectedPlan = {
          plan_id: plan.id,
          type: plan.type,
          plan_name: plan.plan_name,
          mode: mode,
          price: price,
          monthly_price: plan.monthly_price,
          yearly_price: plan.yearly_price,
          currency: plan.currency || 'USD'
        };

        const comparison = this.getPlanComparison(plan.id);
        this.planChangeType = comparison === 'upgrade' ? 'upgrade' :
          comparison === 'downgrade' ? 'downgrade' : '';

        this.isVisible = true;
      }
      else {
        this.selectedPlan = {
          plan_id: plan.id,
          type: plan.type,
          plan_name: plan.plan_name,
          mode: mode,
          price: price
        };
        this.webQrSelectedPlan = null;
        this.isPaymentModal = true;
      }
    }
  }
  handleFreePlanOk() {
    this.isFreePlanModalVisible = false;
    if (this.selectedFreePlan?.id) {
      this.freePlanApi(this.selectedFreePlan.id); // Pass the dynamic ID
    }
  }
  handleFreePlanCancel() {
    this.isFreePlanModalVisible = false;
  }
  EnterprizeSubscribtion() {
    this.enterprizeModal = true
  }

  private prefillEnterpriseForm(): void {
    if (!this.isLoggedIn) return;
    const user = this.user || this.authService.getUserDetails();
    if (!user || !this.enterprizeForm) return;

    const companyName =
      user.isCompany?.[0]?.name ||
      user._company_as_client?.name ||
      user.company_name ||
      user.company?.name ||
      '';

    this.enterprizeForm.patchValue({
      name: user.name || user.full_name || '',
      company_name: companyName,
      email: user.email || '',
      phone_number: user.phone_number || user.phone || user.mobile || ''
    }, { emitEvent: false });
  }
  hasActiveSubscription(): boolean {
    return this.getCurrentPlanId() !== null && !this.remainingTime?.expired;
  }

  getPlanComparison(planId: any): 'upgrade' | 'downgrade' | 'same' | '' {
    if (this.remainingTime?.expired) {
      return '';
    }
    const allPlans = [...this.monthlyPlans, ...this.annualPlans];
    // By id-as-text: a card's id and the subscription's id are uuids, and === between a
    // uuid and anything coerced is false.
    const newPlan = allPlans.find((p) => this.isSameId(p?.id, planId));
    const currentSubscription = this.subscribedPlans;
    const currentPlanId = this.getCurrentPlanId();
    const hasValidSubscription = currentPlanId !== null;
    if (!newPlan || !hasValidSubscription) return 'upgrade';
    if (this.isCurrentPlanId(planId)) {
      const currentMode = currentSubscription.mode?.toLowerCase();
      const selectedMode = this.activeTabId === 'annually' ? 'annually' : 'monthly';

      if ((currentMode === 'annually' || currentMode === 'yearly') && selectedMode === 'annually') {
        return 'same';
      } else if (currentMode !== 'annually' && currentMode !== 'yearly' && selectedMode === 'monthly') {
        return 'same';
      }
      // A free plan has no billing cycle, so it is the current plan on both tabs.
      if (this.isFreePlanCard(newPlan)) {
        return 'same';
      }
    }
    if (newPlan.isEnterprise || currentSubscription.sub_plan?.isEnterprise) {
      if (this.isCurrentPlanId(planId)) {
        return 'same';
      }
      return 'upgrade';
    }
    const selectedCycle: 'monthly_price' | 'yearly_price' =
      this.activeTabId === 'annually' ? 'yearly_price' : 'monthly_price';
    const subscribedCycle: 'monthly_price' | 'yearly_price' =
      (currentSubscription.mode?.toLowerCase() === 'annually' ||
        currentSubscription.mode?.toLowerCase() === 'yearly')
        ? 'yearly_price'
        : 'monthly_price';
    const currentPlan = allPlans.find(p => this.isCurrentPlanId(p.id)) || currentSubscription.sub_plan;
    const currentPrice = this.getPriceValue(currentPlan?.[subscribedCycle]);
    const newPrice = this.getPriceValue(newPlan[selectedCycle]);

    // Another plan on the other tab: rank the two plans on the same cycle. Comparing a yearly
    // price against the current monthly price made every cheaper tier read as an "Upgrade".
    if (subscribedCycle !== selectedCycle && !this.isCurrentPlanId(planId)) {
      const currentPriceOnCycle = this.getPriceValue(currentPlan?.[selectedCycle]);
      if (newPrice > currentPriceOnCycle) return 'upgrade';
      if (newPrice < currentPriceOnCycle) return 'downgrade';
      return 'upgrade';
    }

    if (subscribedCycle !== selectedCycle) {
      if (selectedCycle === 'yearly_price') {
        return newPrice >= currentPrice ? 'upgrade' : 'downgrade';
      } else {
        return newPrice <= currentPrice ? 'downgrade' : 'upgrade';
      }
    }
    if (newPrice > currentPrice) return 'upgrade';
    if (newPrice < currentPrice) return 'downgrade';
    return this.isCurrentPlanId(planId) ? 'same' : 'upgrade';
  }
  private getPriceValue(price: any): number {
    if (price == null || price === undefined) return 0;

    if (typeof price === 'string') {
      const lower = price.toLowerCase().trim();

      if (lower === 'free' || lower === '') return 0;
      if (lower.includes('contact') || lower.includes('enterprise')) return Number.MAX_SAFE_INTEGER;

      const numeric = parseFloat(lower.replace(/[^0-9.]/g, ''));
      return isNaN(numeric) ? 0 : numeric;
    }
    if (typeof price === 'number') return price;
    return 0;
  }
  calculateRemainingTime(plan: any): {
    days: number;
    expired: boolean;
  } {
    if (!plan?.start_date || !plan?.end_date) {
      return { days: 0, expired: true };
    }

    const now = new Date();
    const endDate = new Date(plan.end_date);

    // ✅ End date ko midnight set karein taake pure din ka calculation ho
    endDate.setHours(23, 59, 59, 999);

    const diff = endDate.getTime() - now.getTime();

    if (diff <= 0) {
      return { days: 0, expired: true };
    }

    // ✅ Total seconds calculate karein
    const totalSeconds = Math.floor(diff / 1000);

    // ✅ Days calculate karein
    const days = Math.floor(totalSeconds / (60 * 60 * 24));

    return { days, expired: false };
  }

  /**
   * WHAT THE SDK ADDS TO THIS PURCHASE, on the cycle being bought.
   *
   * Read from the server, not worked out here. sdk_charge_monthly / sdk_charge_yearly come from
   * sdkChargeFor — the same function the checkout charges from — and carry the rules the browser
   * has no way to apply: a monthly add-on billed twelve times on a yearly plan, an annual one
   * riding the month it falls due, a one-time fee charged once ever and never again.
   *
   * `plan.sdk_price` is the UNIT price, and using it here is what made a discounted yearly card
   * quote $100 of SDK where the checkout takes $1,200.
   */
  getEffectiveSdkPrice(plan: any, cycle: 'monthly' | 'yearly' = 'monthly'): number {
    const fromServer = cycle === 'monthly' ? plan?.sdk_charge_monthly : plan?.sdk_charge_yearly;
    if (fromServer !== null && fromServer !== undefined) return Number(fromServer) || 0;

    // Only for a response that predates those fields.
    const sdkPrice = Number(plan?.sdk_price || 0);
    if (!sdkPrice) return 0;
    if (plan?.sdk_billing_cycle === 'one_time' && this.isSamePlan(plan) && !this.isPlanExpired()) {
      return 0;
    }
    return sdkPrice;
  }

  getTotalPrice(plan: any, cycle: 'monthly' | 'yearly'): number {
    // The plan you are ON, in a period you have already paid for, shows what you were charged —
    // not what the plan costs today. An admin adding the SDK to a plan does not change what
    // somebody already bought; they meet the new price when they renew.
    //
    // ONLY for the cycle actually bought. A monthly subscriber looking at the annual card is
    // asking what a year would cost, and answering with the month they paid for reads as an
    // annual price of 100 on a plan that charges 200.
    if (this.isSamePlan(plan) && !this.isPlanExpired() && this.isSubscribedCycle(cycle)) {
      const paid = this.subscribedPlans?.paid_total;
      if (paid !== null && paid !== undefined) return Number(paid);
    }

    // The server sends the real total per cycle — plan plus whatever the SDK adds THIS time,
    // which depends on the two billing cycles and on what this business has already paid.
    // Neither is knowable here, and the figure has to match what the checkout will charge, so
    // it is read rather than recomputed.
    const fromServer = cycle === 'monthly' ? plan?.total_monthly : plan?.total_yearly;
    if (fromServer !== null && fromServer !== undefined) return Number(fromServer);

    // Only for a response that predates those fields.
    const basePrice = cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);
    return basePrice + this.getEffectiveSdkPrice(plan, cycle);
  }

  getDiscountedPrice(price: number, discountType: string, discountValue: number): number {
    if (!discountType || !discountValue) return price;

    if (discountType === 'percentage') {
      return price - (price * discountValue / 100);
    } else if (discountType === 'fixed') {
      return price - discountValue;
    }
    return price;
  }

  formatPositive(value: any): any {
    if (value === null || value === undefined) return value;
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return Math.abs(num);
  }

  private getActiveRenewalDiscount(plan: any): any | null {
    const discounts = plan?.subscription_renewal_discount_of_user_of_billing_payment;
    if (!Array.isArray(discounts) || discounts.length === 0) return null;

    const now = Date.now();
    const active = discounts.find((item: any) => {
      const status = String(item?.status || '').toLowerCase();
      const validFrom = Number(item?.valid_from || 0);
      const validUntil = Number(item?.valid_until || 0);
      const withinValidity =
        validFrom > 0 && validUntil > 0 ? now >= validFrom && now <= validUntil : true;

      return status === 'active' && withinValidity;
    });

    return active || null;
  }

  hasRenewalDiscount(plan: any, cycle: 'monthly' | 'yearly'): boolean {
    const discount = this.getActiveRenewalDiscount(plan);
    if (!discount) return false;

    const discountedAmount = cycle === 'monthly'
      ? Number(discount?.discounted_monthly_amount)
      : Number(discount?.discounted_yearly_amount);
    return !Number.isNaN(discountedAmount) && discountedAmount >= 0;
  }

  getRenewalDiscountAmount(plan: any, cycle: 'monthly' | 'yearly'): number {
    const discount = this.getActiveRenewalDiscount(plan);
    if (!discount) return cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);

    const value = cycle === 'monthly'
      ? Number(discount?.discounted_monthly_amount)
      : Number(discount?.discounted_yearly_amount);
    return Number.isNaN(value) ? 0 : value;
  }

  getOriginalPlanAmount(plan: any, cycle: 'monthly' | 'yearly'): number {
    const discount = this.getActiveRenewalDiscount(plan);
    const fallback = cycle === 'monthly' ? Number(plan?.monthly_price || 0) : Number(plan?.yearly_price || 0);
    if (!discount) return fallback;

    const original = cycle === 'monthly'
      ? Number(discount?.original_monthly_amount)
      : Number(discount?.original_yearly_amount);
    return Number.isNaN(original) ? fallback : original;
  }

  shouldShowStrikethrough(plan: any, cycle: 'monthly' | 'yearly'): boolean {
    const sdkPrice = this.getEffectiveSdkPrice(plan, cycle);
    const originalTotal = this.getOriginalPlanAmount(plan, cycle) + sdkPrice;
    const discountedTotal = this.getRenewalDiscountAmount(plan, cycle) + sdkPrice;
    return originalTotal > discountedTotal;
  }

  getDiscountValue(plan: any): number {
    if (this.activeTabId === 'monthly' || plan.mode === 'monthly') {
      return plan.monthly_discount || 0;
    } else if (this.activeTabId === 'annually' || plan.mode === 'annually' || this.activeTabId === 'yearly') {
      return plan.yearly_discount || 0;
    }
    return 0;
  }

  getPlanPrice(plan: any): number {
    if (this.activeTabId === 'monthly' || plan.mode === 'monthly') {
      return plan.monthly_price;
    } else if (this.activeTabId === 'annually' || plan.mode === 'annually' || this.activeTabId === 'yearly') {
      return plan.yearly_price;
    }
    return 0;
  }
  calculateDiscount(plan: any): { type: string, value: number } {
    const discountValue = this.getDiscountValue(plan);
    return {
      type: plan.discount_type,
      value: discountValue
    };
  }
  openPrivacyPolicy(): void {
    this.modal.create({
      nzTitle: this.translateService.instant('subscription-page.privacy-policy'),
      nzContent: PrivacyPolicyComponent,
      nzFooter: null,
      nzWidth: 1000,
      nzMaskClosable: true,
      nzClosable: true,
      nzWrapClassName: 'custom-modal-animate',
      nzMaskStyle: { backgroundColor: 'rgba(0,0,0,0.6)' },
      nzNoAnimation: false,
      nzBodyStyle: {
        maxHeight: '90vh',
        overflowY: 'auto'
      },
    });
  }
  openTermsConditions(): void {
    this.modal.create({
      nzTitle: this.translateService.instant('subscription-page.terms-conditions'),
      nzContent: TermsConditionComponent,
      nzFooter: null,
      nzWidth: 1000,
      nzMaskClosable: true,
      nzClosable: true,
      nzWrapClassName: 'custom-modal-animate',
      nzMaskStyle: { backgroundColor: 'rgba(0,0,0,0.6)' },
      nzNoAnimation: false,
      nzBodyStyle: {
        maxHeight: '90vh',
        overflowY: 'auto'
      },
    });
  }

}

